/* eslint-disable @typescript-eslint/member-ordering */

import { ChangeDetectionStrategy, Component, inject, output, OutputEmitterRef, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormField, MatInput, MatLabel } from '@angular/material/input';
import { MatList, MatListItem, MatListItemMeta } from '@angular/material/list';
import { IconButtonComponent } from '@libs/common/components';
import { AuthService } from '@libs/shared/auth';
import { UserModel } from '@libs/account/models';
import { AgentService, UserService } from '@libs/shared/services';
import { debounceTime, filter, firstValueFrom } from 'rxjs';

@Component({
  selector: 'ui-agent-search',
  imports: [
    FormsModule,
    IconButtonComponent,
    MatFormField,
    MatInput,
    MatLabel,
    MatList,
    MatListItem,
    MatListItemMeta,
    ReactiveFormsModule,
  ],
  templateUrl: './agent-search.component.html',
  styleUrl: './agent-search.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgentSearchComponent {
  private readonly agentService: AgentService = inject(AgentService);
  private readonly authService: AuthService = inject(AuthService);
  private readonly userService: UserService = inject(UserService);

  public userSelected: OutputEmitterRef<UserModel> = output();

  public searchControl: FormControl = new FormControl<string>('');
  public users: WritableSignal<UserModel[]> = signal([]);

  public constructor() {
    this.searchControl.valueChanges.pipe(
      debounceTime(300),
      takeUntilDestroyed(),
      filter((val: string) => !!val.trim().length),
    ).subscribe((value) => {
      void this.agentSearch(value);
    });
  }

  public async agentSearch(search: string): Promise<void> {
    this.users.set(await firstValueFrom(this.agentService.search(search)));
  }

  public async setUser(user: UserModel): Promise<void> {
    await firstValueFrom(this.authService.agentSetUser(user.id));
    await firstValueFrom(this.userService.getCurrentUser(true));
  }
}
